// RRuleEditor — 三段式 RRULE 編輯器：preset + form + raw + 即時預覽。
//
// 輸出 RRULE-only string（無 DTSTART:），由父層配 valid_from 一起送後端。
// 使用 rrule.js 同步前後端 RRULE 解析；preview 直接 .between() 算未來
// 7 / 30 天觸發點。
//
// Spec: docs/specs/location-tasks.md §6.5.

import React, { useEffect, useMemo, useState } from "react";
import { RRule, rrulestr, Frequency, Weekday } from "rrule";
import { Calendar, Pencil, Eye, ListChecks } from "lucide-react";
import { formatTaipeiDateTime } from "../../lib/time.js";

interface Props {
  value: string;
  onChange: (rrule: string) => void;
  /** Used to scope previews — defaults to now. */
  validFrom?: number;
}

/** 內建 quick presets — 一鍵套用最常見的巡邏排程。 */
const PRESETS: Array<{ id: string; label: string; rrule: string }> = [
  { id: "weekday-9-11-14-16", label: "週一~五 09:00 + 14:00 各一次（共 10/週）",
    rrule: "FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR;BYHOUR=9,14;BYMINUTE=0" },
  { id: "every-day-22-02-06", label: "每天夜班 22:00 / 02:00 / 06:00",
    rrule: "FREQ=DAILY;BYHOUR=22,2,6;BYMINUTE=0" },
  { id: "every-day-08", label: "每天 08:00",
    rrule: "FREQ=DAILY;BYHOUR=8;BYMINUTE=0" },
  { id: "weekly-mon-09", label: "每週一 09:00",
    rrule: "FREQ=WEEKLY;BYDAY=MO;BYHOUR=9;BYMINUTE=0" },
  { id: "monthly-first-mon-09", label: "每月第 1 個週一 09:00",
    rrule: "FREQ=MONTHLY;BYDAY=MO;BYSETPOS=1;BYHOUR=9;BYMINUTE=0" },
  { id: "monthly-day1-09", label: "每月 1 號 09:00",
    rrule: "FREQ=MONTHLY;BYMONTHDAY=1;BYHOUR=9;BYMINUTE=0" },
  { id: "yearly-1111", label: "每年 11 月 11 日 09:00",
    rrule: "FREQ=YEARLY;BYMONTH=11;BYMONTHDAY=11;BYHOUR=9;BYMINUTE=0" },
];

type Tab = "preset" | "form" | "raw";

export default function RRuleEditor({ value, onChange, validFrom }: Props): React.ReactElement {
  const [tab, setTab] = useState<Tab>(value ? "form" : "preset");

  return (
    <div className="rounded-md border border-border bg-bg-raised">
      <header className="flex border-b border-border">
        <TabBtn active={tab === "preset"} onClick={() => setTab("preset")}>
          <ListChecks className="h-3.5 w-3.5" /> 快速範本
        </TabBtn>
        <TabBtn active={tab === "form"} onClick={() => setTab("form")}>
          <Pencil className="h-3.5 w-3.5" /> 表單編輯
        </TabBtn>
        <TabBtn active={tab === "raw"} onClick={() => setTab("raw")}>
          <Calendar className="h-3.5 w-3.5" /> 原始 RRULE
        </TabBtn>
      </header>
      <div className="p-3">
        {tab === "preset" && <PresetTab value={value} onChange={onChange} />}
        {tab === "form" && <FormTab value={value} onChange={onChange} />}
        {tab === "raw" && <RawTab value={value} onChange={onChange} />}
      </div>
      <div className="border-t border-border bg-bg-surface p-3">
        <Preview value={value} validFrom={validFrom ?? Math.floor(Date.now() / 1000)} />
      </div>
    </div>
  );
}

function TabBtn({ children, active, onClick }: { children: React.ReactNode; active: boolean; onClick: () => void }): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 h-9 inline-flex items-center justify-center gap-1.5 text-xs font-medium border-b-2 transition-colors ${
        active ? "border-accent text-accent" : "border-transparent text-text-secondary hover:text-text-primary"
      }`}
    >
      {children}
    </button>
  );
}

// ── Preset tab ───────────────────────────────────────────────────────────

function PresetTab({ value, onChange }: { value: string; onChange: (s: string) => void }): React.ReactElement {
  return (
    <div className="space-y-2">
      <p className="text-2xs text-text-muted">點選下方任一範本套用，之後可切到「表單編輯」微調。</p>
      {PRESETS.map((p) => {
        const active = value === p.rrule;
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => onChange(p.rrule)}
            className={`w-full text-left rounded-md p-3 transition-colors ${
              active ? "bg-accent/15 border border-accent/40" : "bg-bg-base border border-transparent hover:bg-bg-hover"
            }`}
          >
            <p className="text-sm font-medium">{p.label}</p>
            <p className="text-2xs font-mono text-text-muted mt-0.5">{p.rrule}</p>
          </button>
        );
      })}
    </div>
  );
}

// ── Form tab ─────────────────────────────────────────────────────────────

function FormTab({ value, onChange }: { value: string; onChange: (s: string) => void }): React.ReactElement {
  const parsed = useMemo(() => parseRRule(value), [value]);
  const [freq, setFreq] = useState<Frequency>(parsed.freq);
  // String state so the user can clear the input mid-edit. Parsed back
  // to int with `parseInt(... ) || 1` when composing the RRULE.
  const [interval, setInterval] = useState<string>(String(parsed.interval));
  const [byday, setByday] = useState<Set<number>>(new Set(parsed.byday)); // 0=Mon..6=Sun
  const [bymonthday, setBymonthday] = useState<Set<number>>(new Set(parsed.bymonthday));
  const [byhour, setByhour] = useState<Set<number>>(new Set(parsed.byhour));
  const [byminute, setByminute] = useState<number>(parsed.byminute);
  const [count, setCount] = useState<number | "">(parsed.count ?? "");
  const [until, setUntil] = useState<string>(parsed.until ? toDateInput(parsed.until) : "");

  // Re-hydrate when value changes externally (e.g. switching tabs after preset).
  useEffect(() => {
    const p = parseRRule(value);
    setFreq(p.freq);
    setInterval(String(p.interval));
    setByday(new Set(p.byday));
    setBymonthday(new Set(p.bymonthday));
    setByhour(new Set(p.byhour));
    setByminute(p.byminute);
    setCount(p.count ?? "");
    setUntil(p.until ? toDateInput(p.until) : "");
  }, [value]);

  // Compose RRULE string from form state and emit upstream.
  useEffect(() => {
    const parts: string[] = [`FREQ=${frequencyToken(freq)}`];
    const intervalN = Math.max(1, parseInt(interval, 10) || 1);
    if (intervalN > 1) parts.push(`INTERVAL=${intervalN}`);
    if (byday.size > 0 && (freq === RRule.WEEKLY || freq === RRule.MONTHLY)) {
      parts.push(`BYDAY=${[...byday].sort().map(idxToWeekday).join(",")}`);
    }
    if (bymonthday.size > 0 && (freq === RRule.MONTHLY || freq === RRule.YEARLY)) {
      parts.push(`BYMONTHDAY=${[...bymonthday].sort((a, b) => a - b).join(",")}`);
    }
    if (byhour.size > 0) parts.push(`BYHOUR=${[...byhour].sort((a, b) => a - b).join(",")}`);
    parts.push(`BYMINUTE=${byminute}`);
    if (typeof count === "number" && count > 0) parts.push(`COUNT=${count}`);
    else if (until) parts.push(`UNTIL=${rfc5545FromDateInput(until)}`);
    onChange(parts.join(";"));
    // onChange is stable per parent; intentionally not in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freq, interval, byday, bymonthday, byhour, byminute, count, until]);

  return (
    <div className="space-y-3">
      <div>
        <Label>頻率</Label>
        <select
          className="select"
          value={String(freq)}
          onChange={(e) => setFreq(Number(e.target.value) as Frequency)}
        >
          <option value={String(RRule.DAILY)}>每天</option>
          <option value={String(RRule.WEEKLY)}>每週</option>
          <option value={String(RRule.MONTHLY)}>每月</option>
          <option value={String(RRule.YEARLY)}>每年</option>
        </select>
      </div>
      <div>
        <Label>間隔（每 N 個{frequencyLabel(freq)}）</Label>
        <input
          type="number"
          min={1}
          max={365}
          className="input font-mono"
          value={interval}
          onChange={(e) => setInterval(e.target.value)}
        />
      </div>

      {(freq === RRule.WEEKLY || freq === RRule.MONTHLY) && (
        <div>
          <Label>週幾</Label>
          <div className="flex flex-wrap gap-1">
            {WEEKDAYS.map((w, i) => {
              const on = byday.has(i);
              return (
                <button
                  key={w}
                  type="button"
                  onClick={() => {
                    const next = new Set(byday);
                    on ? next.delete(i) : next.add(i);
                    setByday(next);
                  }}
                  className={`h-8 px-3 rounded-md text-xs font-medium ${
                    on ? "bg-accent text-text-inverse" : "bg-bg-base text-text-secondary hover:bg-bg-hover"
                  }`}
                >
                  {w}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {(freq === RRule.MONTHLY || freq === RRule.YEARLY) && (
        <div>
          <Label>每月幾號</Label>
          <ChipPicker max={31} start={1} values={bymonthday} onChange={setBymonthday} cols={7} />
        </div>
      )}

      <div>
        <Label>觸發小時（24 小時制，可多選）</Label>
        <ChipPicker max={24} start={0} values={byhour} onChange={setByhour} cols={6} pad={2} />
      </div>

      <div>
        <Label>觸發分鐘</Label>
        <select
          className="select"
          value={String(byminute)}
          onChange={(e) => setByminute(Number(e.target.value))}
        >
          {[0, 15, 30, 45].map((m) => <option key={m} value={String(m)}>{String(m).padStart(2, "0")} 分</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>次數上限（COUNT）</Label>
          <input
            type="number"
            min={1}
            className="input font-mono"
            placeholder="留空 = 無上限"
            value={count}
            onChange={(e) => {
              const n = e.target.value ? Number(e.target.value) : "";
              setCount(typeof n === "number" && n > 0 ? n : "");
              if (typeof n === "number" && n > 0) setUntil("");
            }}
          />
        </div>
        <div>
          <Label>截止日期（UNTIL）</Label>
          <input
            type="date"
            className="input"
            value={until}
            onChange={(e) => {
              setUntil(e.target.value);
              if (e.target.value) setCount("");
            }}
          />
        </div>
      </div>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }): React.ReactElement {
  return <p className="text-2xs uppercase tracking-wider text-text-muted mb-1.5">{children}</p>;
}

function ChipPicker({ start, max, values, onChange, cols, pad = 0 }: {
  start: number; max: number; values: Set<number>; onChange: (v: Set<number>) => void; cols: number; pad?: number;
}): React.ReactElement {
  const items: number[] = [];
  for (let i = start; i < start + max; i++) items.push(i);
  return (
    <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
      {items.map((i) => {
        const on = values.has(i);
        return (
          <button
            key={i}
            type="button"
            onClick={() => {
              const next = new Set(values);
              on ? next.delete(i) : next.add(i);
              onChange(next);
            }}
            className={`h-7 text-xs font-mono rounded ${
              on ? "bg-accent text-text-inverse" : "bg-bg-base text-text-secondary hover:bg-bg-hover"
            }`}
          >
            {pad ? String(i).padStart(pad, "0") : i}
          </button>
        );
      })}
    </div>
  );
}

// ── Raw tab ──────────────────────────────────────────────────────────────

function RawTab({ value, onChange }: { value: string; onChange: (s: string) => void }): React.ReactElement {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <div className="space-y-2">
      <p className="text-2xs text-text-muted">直接編輯 RFC 5545 RRULE 字串。修改後按「套用」。</p>
      <textarea
        className="input font-mono text-xs"
        rows={3}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR;BYHOUR=9;BYMINUTE=0"
      />
      <button
        type="button"
        onClick={() => onChange(draft.trim())}
        className="btn-secondary text-xs"
      >
        套用
      </button>
    </div>
  );
}

// ── Preview ──────────────────────────────────────────────────────────────

function Preview({ value, validFrom }: { value: string; validFrom: number }): React.ReactElement {
  const occurrences = useMemo(() => {
    if (!value || !value.trim()) return [];
    try {
      const r = rrulestr(value.includes("DTSTART") ? value : `RRULE:${value}`);
      const start = new Date(validFrom * 1000);
      const end = new Date(start.getTime() + 7 * 86400_000);
      return r.between(start, end, true);
    } catch {
      return null;
    }
  }, [value, validFrom]);

  return (
    <div>
      <div className="flex items-center gap-1.5 text-2xs text-text-muted mb-2">
        <Eye className="h-3 w-3" />
        未來 7 天觸發預覽（從生效時間起算）
      </div>
      {occurrences === null ? (
        <p className="text-xs text-danger">RRULE 解析失敗，請檢查格式。</p>
      ) : occurrences.length === 0 ? (
        <p className="text-xs text-warning">未來 7 天沒有觸發點，請確認 RRULE 是否正確。</p>
      ) : (
        <ul className="text-xs space-y-1 max-h-32 overflow-y-auto pr-2">
          {occurrences.slice(0, 12).map((d, i) => (
            <li key={i} className="font-mono text-text-secondary">
              {formatTaipeiDateTime(d)}
            </li>
          ))}
          {occurrences.length > 12 && (
            <li className="text-text-muted">…再 {occurrences.length - 12} 個</li>
          )}
        </ul>
      )}
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────────────────

const WEEKDAYS = ["一", "二", "三", "四", "五", "六", "日"];
function idxToWeekday(i: number): string {
  // 0..6 = Mon..Sun (matches our chip order); rrule.js wants MO,TU,WE,TH,FR,SA,SU.
  return ["MO", "TU", "WE", "TH", "FR", "SA", "SU"][i];
}
function weekdayToIdx(w: Weekday): number {
  return ["MO", "TU", "WE", "TH", "FR", "SA", "SU"].indexOf(String(w));
}
/** rrule.js's parseString returns ByWeekday which can be string | number |
 *  Weekday — normalise to 0..6 (Mon..Sun). Returns -1 on unrecognised. */
function normaliseWeekday(w: unknown): number {
  if (typeof w === "number") return w;
  const s = String(w).toUpperCase();
  return ["MO", "TU", "WE", "TH", "FR", "SA", "SU"].indexOf(s);
}
function frequencyToken(f: Frequency): string {
  return ["YEARLY", "MONTHLY", "WEEKLY", "DAILY", "HOURLY", "MINUTELY", "SECONDLY"][f];
}
function frequencyLabel(f: Frequency): string {
  switch (f) {
    case RRule.DAILY: return "天";
    case RRule.WEEKLY: return "週";
    case RRule.MONTHLY: return "月";
    case RRule.YEARLY: return "年";
    default: return "週期";
  }
}

interface ParsedRRule {
  freq: Frequency;
  interval: number;
  byday: number[];
  bymonthday: number[];
  byhour: number[];
  byminute: number;
  count: number | null;
  until: Date | null;
}

function parseRRule(rrule: string): ParsedRRule {
  const fallback: ParsedRRule = {
    freq: RRule.DAILY, interval: 1, byday: [], bymonthday: [],
    byhour: [9], byminute: 0, count: null, until: null,
  };
  if (!rrule || !rrule.trim()) return fallback;
  try {
    const o = RRule.parseString(rrule);
    const byweekdayRaw = o.byweekday;
    const byweekdayArrRaw: unknown[] = Array.isArray(byweekdayRaw)
      ? byweekdayRaw
      : byweekdayRaw !== undefined && byweekdayRaw !== null ? [byweekdayRaw] : [];
    const byweekdayArr = byweekdayArrRaw
      .map((w) => normaliseWeekday(w))
      .filter((n) => n >= 0);
    const bymonthdayArr: number[] = Array.isArray(o.bymonthday)
      ? o.bymonthday
      : typeof o.bymonthday === "number" ? [o.bymonthday] : [];
    const byhourArr: number[] = Array.isArray(o.byhour)
      ? o.byhour
      : typeof o.byhour === "number" ? [o.byhour] : [9];
    const byminuteVal = Array.isArray(o.byminute)
      ? (o.byminute[0] ?? 0)
      : typeof o.byminute === "number" ? o.byminute : 0;
    return {
      freq: o.freq ?? RRule.DAILY,
      interval: o.interval ?? 1,
      byday: byweekdayArr,
      bymonthday: bymonthdayArr,
      byhour: byhourArr,
      byminute: byminuteVal,
      count: o.count ?? null,
      until: o.until ?? null,
    };
  } catch {
    return fallback;
  }
}

function toDateInput(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function rfc5545FromDateInput(s: string): string {
  return `${s.replace(/-/g, "")}T235959Z`;
}
